/**
 * @fileoverview Quick-add task status dialog (MOR-696)
 *
 * A lightweight dialog for creating a firm-defined task status without leaving
 * the task side-panel. Mirrors the create form in the Task Statuses manager
 * (name / type / colour) and calls the same `tasks.statuses.create` mutation.
 * On success it hands the new status id back to the caller so the dropdown can
 * immediately apply it to the current task.
 *
 * Admin/owner only — the caller gates the entry point and the mutation enforces
 * the same rule server-side.
 */

import { useState } from "react";
import { useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskStatusType } from "@cvx/tasks/taskStatuses/schema";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { extractErrorMessage } from "@/utils/errors";

import { ColorSwatchPicker } from "./ColorSwatchPicker";
import { STATUS_TYPE_OPTIONS } from "./status-types";

export function QuickAddStatusDialog({
  orgId,
  open,
  onOpenChange,
  onCreated,
}: {
  orgId: Id<"organizations">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new status id once it has been created. */
  onCreated?: (statusId: Id<"taskStatuses">) => void;
}) {
  const create = useConvexMutation(api.tasks.statuses.create);

  const [name, setName] = useState("");
  const [type, setType] = useState<TaskStatusType>("unstarted");
  const [color, setColor] = useState<TaskColor>("gray");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName("");
    setType("unstarted");
    setColor("gray");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Status name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const statusId = await create({ orgId, name: trimmed, type, color });
      toast.success(`Added status "${trimmed}".`);
      onCreated?.(statusId);
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not add status."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">New status</DialogTitle>
          <DialogDescription className="text-xs">
            Add a workflow status for your firm. Manage all of them on the Task
            Statuses page.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Name
              </Label>
              <Input
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submit();
                  }
                }}
                placeholder="In Review, Blocked…"
                className="h-8 text-xs"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs font-medium text-muted-foreground">
                Type
              </Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as TaskStatusType)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_TYPE_OPTIONS.map((t) => (
                    <SelectItem
                      key={t.value}
                      value={t.value}
                      className="text-xs"
                    >
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs font-medium text-muted-foreground">
              Colour
            </Label>
            <ColorSwatchPicker value={color} onChange={setColor} />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            size="sm"
            className="gap-1.5 text-xs"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus className="size-3.5" />
            )}
            Add status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
